import { makeRng, type Rng } from "../../../shared/rng";
import { distToSegment, visited, type Pt, type StarsSpec } from "./geometry";

// ---- lattice puzzles: n x n stars, the famous "nine dots" and its bigger cousins ----

const SPACING: Record<number, number> = { 3: 18, 4: 15, 5: 12 };

/** solutions are given in lattice coordinates (star (i, j) sits at integer i, j; corners may lie outside) */
const LATTICE_SOLUTIONS: Record<number, { lines: number; trail: [number, number][] }> = {
  3: { lines: 4, trail: [[0, 0], [3, 0], [0, 3], [0, 0], [2, 2]] },
  4: { lines: 6, trail: [[0, 0], [3, 0], [3, 3], [-1, 3], [2, 0], [2, 3], [0, 1]] }
};

export function latticeSpec(n: 3 | 4): StarsSpec {
  const s = SPACING[n];
  const at = (i: number, j: number): Pt => ({ x: 50 + (i - (n - 1) / 2) * s, y: 50 + (j - (n - 1) / 2) * s });
  const stars: Pt[] = [];
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) stars.push(at(i, j));
  const sol = LATTICE_SOLUTIONS[n];
  return {
    kind: "lattice",
    stars,
    lines: sol.lines,
    tol: 0.14 * s,
    snapStep: s / 2,
    snapOrigin: at(0, 0),
    starSnap: 0.24 * s,
    solution: sol.trail.map(([i, j]) => at(i, j)),
    title: `Visit all ${n * n} stars using only ${sol.lines} straight lines, without lifting your pen`
  };
}

// ---- hidden paths: a constellation made by hiding a zig-zag trail among random stars ----

interface Level {
  lines: number;
  perLine: readonly number[];
}

const LEVELS: Record<number, Level> = {
  1: { lines: 3, perLine: [3, 3, 3] },
  2: { lines: 4, perLine: [3, 3, 3, 3] },
  3: { lines: 5, perLine: [3, 2, 3, 2, 3] },
  4: { lines: 6, perLine: [3, 2, 3, 2, 3, 2] },
  5: { lines: 7, perLine: [2, 2, 3, 2, 2, 2, 2] }
};

const TOL = 2.8;
const MIN_STAR_GAP = 10;
const MIN_VERTEX_GAP = 8;
const MIN_OTHER_LINE = 5;
const MIN_LINE = 32;

/** corners of a zig-zag: every line long enough to carry stars, every turn clearly neither a hairpin nor almost straight */
function randomTrail(rng: Rng, lines: number): Pt[] | null {
  const pts: Pt[] = [{ x: rng.range(8, 92), y: rng.range(8, 92) }];
  for (let i = 0; i < lines; i++) {
    const prev = pts[i];
    let next: Pt | null = null;
    for (let t = 0; t < 120 && !next; t++) {
      const p = { x: rng.range(6, 94), y: rng.range(6, 94) };
      if (Math.hypot(p.x - prev.x, p.y - prev.y) < MIN_LINE) continue;
      if (i > 0) {
        const before = pts[i - 1];
        let d = Math.abs(Math.atan2(before.y - prev.y, before.x - prev.x) - Math.atan2(p.y - prev.y, p.x - prev.x));
        if (d > Math.PI) d = 2 * Math.PI - d; // interior angle at the corner, 0..pi
        if (d < 0.6 || d > 2.6) continue;
      }
      next = p;
    }
    if (!next) return null;
    pts.push(next);
  }
  return pts;
}

function tryConstellation(rng: Rng, level: Level): { stars: Pt[]; trail: Pt[] } | null {
  const trail = randomTrail(rng, level.lines);
  if (!trail) return null;
  // stars scattered along the lines: never on a corner, never crowded, never ambiguous
  const stars: Pt[] = [];
  for (let i = 0; i < level.lines; i++) {
    const a = trail[i];
    const b = trail[i + 1];
    const ts: number[] = [];
    for (let s = 0; s < level.perLine[i]; s++) {
      let placed = false;
      for (let t = 0; t < 80 && !placed; t++) {
        const u = rng.range(0.14, 0.86);
        if (ts.some((o) => Math.abs(o - u) < 0.22)) continue;
        const p = { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
        if (p.x < 8 || p.x > 92 || p.y < 8 || p.y > 92) continue;
        if (stars.some((o) => Math.hypot(o.x - p.x, o.y - p.y) < MIN_STAR_GAP)) continue;
        if (trail.some((v) => Math.hypot(v.x - p.x, v.y - p.y) < MIN_VERTEX_GAP)) continue;
        // keep it off the other lines so every star clearly belongs to one line
        let clear = true;
        for (let j = 0; j < level.lines && clear; j++) {
          if (j !== i && distToSegment(p, trail[j], trail[j + 1]) < MIN_OTHER_LINE) clear = false;
        }
        if (!clear) continue;
        ts.push(u);
        stars.push(p);
        placed = true;
      }
      if (!placed) return null;
    }
  }
  // sanity: the hidden trail really visits every star
  if (!visited(stars, trail, TOL).every(Boolean)) return null;
  return { stars, trail };
}

export function generateConstellation(seed: string, difficulty: number): StarsSpec {
  const level = LEVELS[Math.max(1, Math.min(5, difficulty))];
  const rng = makeRng("stars|" + seed);
  for (let attempt = 0; attempt < 1500; attempt++) {
    const found = tryConstellation(rng, level);
    if (!found) continue;
    const order = rng.shuffle(found.stars.map((_, i) => i));
    const round = (p: Pt): Pt => ({ x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 });
    return {
      kind: "constellation",
      stars: order.map((i) => round(found.stars[i])),
      lines: level.lines,
      tol: TOL,
      snapStep: null,
      snapOrigin: { x: 0, y: 0 },
      starSnap: 4,
      solution: found.trail.map(round),
      title: `Visit all ${found.stars.length} stars using only ${level.lines} straight lines`
    };
  }
  // extremely unlikely; keep the app usable
  return latticeSpec(3);
}
